import React, { useMemo } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaFileInvoiceDollar,
  FaRedo,
  FaTimesCircle,
  FaWallet,
} from "react-icons/fa";
import SectionCard from "./../ui/SectionCard";

const money = (value) =>
  Number(value || 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  });

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
  }).format(d);
};

const statusLabel = (status) => {
  switch (status) {
    case "draft":
      return "Brouillon";
    case "submitted":
      return "Soumise";
    case "approved":
      return "Approuvée";
    case "rejected":
      return "Rejetée";
    case "paid":
      return "Payée";
    default:
      return status || "—";
  }
};

const statusClass = (status) => {
  switch (status) {
    case "draft":
      return "bg-slate-100 text-slate-700 ring-slate-200";
    case "submitted":
      return "bg-amber-100 text-amber-800 ring-amber-200";
    case "approved":
      return "bg-emerald-100 text-emerald-800 ring-emerald-200";
    case "rejected":
      return "bg-rose-100 text-rose-800 ring-rose-200";
    case "paid":
      return "bg-sky-100 text-sky-800 ring-sky-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
};

function StatChip({ icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-slate-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
  };

  return (
    <div
      className={`rounded-2xl px-4 py-3 ring-1 ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}

export default function UserExpenseClaimsHistoryCard({
  claims = [],
  loading = false,
  onRefresh,
}) {
  const stats = useMemo(() => {
    const draft = claims.filter((c) => c.statut === "draft").length;
    const submitted = claims.filter((c) => c.statut === "submitted").length;
    const approved = claims.filter((c) => c.statut === "approved").length;
    const rejected = claims.filter((c) => c.statut === "rejected").length;
    const paid = claims.filter((c) => c.statut === "paid").length;

    const totalAmount = claims.reduce(
      (acc, c) => acc + Number(c.total_general || 0),
      0,
    );

    return {
      total: claims.length,
      draft,
      submitted,
      approved,
      rejected,
      paid,
      totalAmount,
    };
  }, [claims]);

  const sortedClaims = useMemo(() => {
    return [...claims].sort((a, b) => {
      const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [claims]);

  return (
    <SectionCard
      id="mes-fiches"
      title="Mes fiches de frais"
      subtitle="Retrouvez ici toutes vos fiches de frais avec leur statut et les principaux montants."
      icon={<FaFileInvoiceDollar />}
      actions={
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
        >
          <FaRedo />
          {loading ? "Chargement..." : "Actualiser"}
        </button>
      }
    >
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
        <StatChip
          icon={<FaFileInvoiceDollar />}
          label="Total"
          value={stats.total}
          tone="slate"
        />
        <StatChip
          icon={<FaClock />}
          label="Soumises"
          value={stats.submitted}
          tone="amber"
        />
        <StatChip
          icon={<FaCheckCircle />}
          label="Approuvées"
          value={stats.approved}
          tone="emerald"
        />
        <StatChip
          icon={<FaTimesCircle />}
          label="Rejetées"
          value={stats.rejected}
          tone="rose"
        />
        <StatChip
          icon={<FaWallet />}
          label="Payées"
          value={stats.paid}
          tone="sky"
        />
        <StatChip
          icon={<FaEuroSign />}
          label="Montant total"
          value={money(stats.totalAmount)}
          tone="slate"
        />
      </div>

      {loading ? (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500">
          Chargement des fiches de frais…
        </div>
      ) : !sortedClaims.length ? (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <div className="text-lg font-semibold text-slate-900">
            Aucune fiche de frais
          </div>
          <div className="mt-2 text-sm text-slate-500">
            Vous n’avez pas encore enregistré de fiche.
          </div>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-slate-200">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">#</th>
                  <th className="px-4 py-3 text-left font-semibold">Événement</th>
                  <th className="px-4 py-3 text-left font-semibold">Date événement</th>
                  <th className="px-4 py-3 text-left font-semibold">Créée le</th>
                  <th className="px-4 py-3 text-left font-semibold">Statut</th>
                  <th className="px-4 py-3 text-right font-semibold">Trajet</th>
                  <th className="px-4 py-3 text-right font-semibold">Autres frais</th>
                  <th className="px-4 py-3 text-right font-semibold">Total</th>
                  <th className="px-4 py-3 text-left font-semibold">Commentaire admin</th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {sortedClaims.map((claim) => {
                  const eventLabel =
                    claim?.ClaimEvent?.titre ||
                    claim?.ClaimEvent?.nom_event ||
                    claim?.ClaimEvent?.nom ||
                    claim?.ClaimEvent?.title ||
                    `Événement #${claim?.event_id || "?"}`;

                  const eventDate =
                    claim?.ClaimEvent?.start ||
                    claim?.ClaimEvent?.date_event ||
                    null;

                  return (
                    <tr
                      key={claim.id}
                      className="border-t border-slate-100 align-top"
                    >
                      <td className="px-4 py-4 font-bold text-slate-900">
                        #{claim.id}
                      </td>

                      <td className="px-4 py-4">
                        <div className="font-semibold text-slate-900">
                          {eventLabel}
                        </div>
                        {claim?.ClaimEvent?.lieu_event ? (
                          <div className="mt-1 text-xs text-slate-500">
                            {claim.ClaimEvent.lieu_event}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-4 py-4 text-slate-600">
                        {fmtDate(eventDate)}
                      </td>

                      <td className="px-4 py-4 text-slate-600">
                        {fmtDate(claim.created_at)}
                      </td>

                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass(claim.statut)}`}
                        >
                          {statusLabel(claim.statut)}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right font-semibold text-slate-700">
                        {money(claim.montant_trajet)}
                      </td>

                      <td className="px-4 py-4 text-right font-semibold text-slate-700">
                        {money(claim.total_frais)}
                      </td>

                      <td className="px-4 py-4 text-right font-black text-slate-900">
                        {money(claim.total_general)}
                      </td>

                      <td className="px-4 py-4">
                        <div className="max-w-[260px] whitespace-pre-line text-sm text-slate-600">
                          {claim.admin_commentaire || "—"}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </SectionCard>
  );
}